import type { Maybe } from '@adornis/base/utilTypes.js';
import { toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { type BillingGroupLike, getBillingGroup } from '../../billingPositions/api/effectiveBillingPosition.js';
import { BillingRhythm } from '../../billingPositions/entities/BillingPosition.js';
import type { Booking } from '../../bookings/entities/Booking.js';
import { ClassBooking } from '../../bookings/entities/ClassBooking.js';
import { EmployeeBooking } from '../../bookings/entities/EmployeeBooking.js';
import { getMandateEndDay } from '../../sepa/api/mandateActivity.js';
import { getSchoolYear } from '../../schoolyear/getSchoolYear.js';
import { getMandatePhase, LIVE_PHASES, type MandatePhaseSubject } from '../../sepa/api/MandatStatus.js';

type ExitBooking = Pick<Booking, '_id' | '_class' | 'groupIDs' | 'endDate'> & { groups?: Maybe<BillingGroupLike[]> };
type ExitMandate = MandatePhaseSubject & { bookingID?: Maybe<string> };

/**
 * Whether an Austritt on `endDate` changes the booking: it runs past that day or has no end - which
 * includes a booking that only starts later, it then runs on no day. An EmployeeBooking follows its
 * contract (`syncEmployeeBookings`), ending it by hand would not last.
 */
export function isEndableOnExit(booking: Pick<Booking, '_class' | 'endDate'>, endDate: Date): boolean {
  if (booking._class === EmployeeBooking._class) return false;
  const end = toCalendarDay(booking.endDate);

  return !end || end > toCalendarDay(endDate);
}

/**
 * Whether the Austritt deletes the booking instead of ending it: a class booking that has not started
 * and starts in a later school year than the end date. A class booking spans exactly one school year
 * (`bookingSchoolyearHook` refuses anything else), so it cannot end before its year begins - and kept,
 * it would make the student a Schüler again on its first day. Nothing was collected under it yet.
 */
export function isRemovedOnExit(booking: Pick<Booking, '_class' | 'startDate'>, endDate: Date): boolean {
  const start = toCalendarDay(booking.startDate);
  const lastDay = toCalendarDay(endDate);
  if (booking._class !== ClassBooking._class || !start || start <= lastDay || start <= todayAsCalendarDay())
    return false;

  return getSchoolYear({ date: start }) !== getSchoolYear({ date: lastDay });
}

const isOpenOneTimeMandate = (mandate: ExitMandate) =>
  mandate.rhythm === BillingRhythm.ONCE_CUSTOM && LIVE_PHASES.includes(getMandatePhase(mandate));

/**
 * Where the switch of a booking starts in the dialog, and why. A booking is billed by exactly one
 * group (`getBillingGroup`), so its rhythm decides: the class booking with its Elternengagement and
 * everything billed monthly or yearly ends with the student. A one-time booking - a Klassenfahrt, an
 * Ausflug - still has to be paid, so it stays open for staff to end once it is; ending it would end
 * its mandate before the collection (`clampMandatesToBookings`). Staff can flip every switch.
 */
export function getExitDefault(booking: ExitBooking, mandates: ExitMandate[]): { ends: boolean; reason: string } {
  const billingGroup = getBillingGroup(booking, booking.groups ?? []);
  const rhythm = billingGroup?.billingPosition?.rhythm;

  if (rhythm === BillingRhythm.ONCE_CUSTOM)
    return { ends: false, reason: 'Einmalige Abrechnung, wird noch eingezogen' };
  // a guard for odd data: whatever the group says now, an open one-time mandate still has to be collected
  if (mandates.some(mandate => mandate.bookingID === booking._id && isOpenOneTimeMandate(mandate)))
    return { ends: false, reason: 'Trägt ein noch nicht eingezogenes einmaliges Mandat' };
  if (booking._class === ClassBooking._class) return { ends: true, reason: 'Klassenmitgliedschaft' };
  if (rhythm === BillingRhythm.MONTHLY) return { ends: true, reason: 'Monatliche Abrechnung' };
  if (rhythm === BillingRhythm.YEARLY) return { ends: true, reason: 'Jährliche Abrechnung' };

  return { ends: false, reason: billingGroup ? 'Rhythmus frei wählbar' : 'Keine eindeutige Abrechnungsposition' };
}

/**
 * The end a mandate of an ending booking gets, or null when it keeps its runtime. Mirrors both writers
 * of the Austritt: the booking hook for fixed amounts (`clampToBookingRuntime`) and `endMandatesNow`
 * for pledges - each leaves {@link getMandateEndDay} and only ever shortens.
 */
export function getMandateEndOnExit(mandate: ExitMandate, endDate: Date): Date | null {
  if (!LIVE_PHASES.includes(getMandatePhase(mandate))) return null;
  const end = getMandateEndDay(toCalendarDay(endDate), mandate.start);

  return mandate.end && mandate.end <= end ? null : end;
}
